<!--
 * @Description: 新增/编辑销售名片弹窗
 * @Author: mdb
 * @Date: 2023-04-12 11:09:47
 * @LastEditTime: 2023-04-25 15:06:59
 * @LastEditors: mdb
-->
<template>
  <cos-modal
    v-model:visible="visible"
    :width="600"
    :title="modalTitle"
    :confirmLoading="loading"
    destroyOnClose
    @ok="handleOk"
    @cancel="handleCancel"
  >
    <a-form :wrapper-col="{ span: 24 }" ref="formRef" :model="dataForm">
      <a-form-item label="销售员" name="follower" :rules="formRules.select">
        <UserSelect v-model:value="dataForm.follower" :status="1" allowClear />
      </a-form-item>
      <a-form-item label="上传名片" name="fileList" :rules="formRules.upload">
        <a-upload
          v-model:file-list="dataForm.fileList"
          action="/base/resource/file/upload"
          list-type="picture-card"
          :maxCount="1"
          name="files"
          :before-upload="beforeUpload"
          :customRequest="backgroundImagesRequest"
          @preview="handlePreview"
        >
          <div v-if="dataForm.fileList && dataForm.fileList.length < 1">
            <plus-outlined />
            <div style="margin-top: 8px">上传</div>
          </div>
        </a-upload>
      </a-form-item>
    </a-form>

    <a-modal :visible="previewVisible" :title="previewTitle" :footer="null" @cancel="handlePreviewCancel">
      <div style="max-height: calc(100vh - 300px); overflow-y: auto">
        <img alt="example" style="width: 100%" :src="previewImage" />
      </div>
    </a-modal>
  </cos-modal>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { PlusOutlined } from '@ant-design/icons-vue'
import { message as DcMessage } from '@dc-platform/dc-components'
import { uploadFileApi } from '@/api/common'
import { businessCardCreate, businessCardUpdate } from '@/api/marketing/marketingManage'
import type { BusinessCardEditParams, BusinessCardListItem } from '@/typings/api/marketingManage'
import type { UploadFile } from 'ant-design-vue'

const initDefaultData = () => ({ fileList: [] })
const formRef = ref()
const visible = ref<boolean>(false)
const loading = ref<boolean>(false)
const dataForm = reactive<BusinessCardEditParams>(initDefaultData())
const formRules = reactive({
  select: [{ required: true, message: '请选择' }],
  upload: [{ required: true, message: '请上传' }]
})
const emit = defineEmits(['fetch'])

const rowsProps = ref<BusinessCardListItem>()
// 弹窗标题
const modalTitle = computed(() => (rowsProps.value ? '编辑销售名片' : '新增销售名片'))
const init = (rows?: BusinessCardListItem) => {
  rowsProps.value = rows
  if (rows) {
    dataForm.id = rows.id
    dataForm.follower = rows.follower
    dataForm.fileList = [{ url: rows.businessCardPath }]
    dataForm.businessCardPath = rows.businessCardPath
  } else {
    for (const key in dataForm) {
      dataForm[key] = undefined
    }
    Object.assign(dataForm, initDefaultData())
  }
  visible.value = true
}
const beforeUpload = (file: UploadFile) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
  if (!isJpgOrPng) DcMessage.error('您只能上传 JPG 或者 PNG 文件!')
  const isLt10M = file.size && file.size / 1024 / 1024 < 10
  if (!isLt10M) DcMessage.error('图片必须小于 10MB !')
  return isJpgOrPng && !!isLt10M
}
const customRequest = (options: any) => {
  return new Promise((resolve, reject) => {
    const formData = new FormData()
    formData.append('files', options.file)
    uploadFileApi(formData)
      .then((res: any) => {
        if (res.code === 200) {
          resolve(res.data)
        } else {
          DcMessage.error(res.message)
          reject(res.message)
        }
      })
      .catch((err) => {
        reject(err)
      })
  })
}
const backgroundImagesRequest = (options: any) => {
  customRequest(options)
    .then((res: any) => {
      dataForm.businessCardPath = res[0]
      dataForm.fileList = [{ url: res[0] }]
    })
    .catch(() => {
      dataForm.businessCardPath = ''
      dataForm.fileList = []
    })
    .finally(() => {
      formRef.value?.validateFields(['fileList'])
    })
}
// 预览图片
function getBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })
}
const previewVisible = ref(false)
const previewImage = ref('')
const previewTitle = ref('')
const handlePreviewCancel = () => {
  previewVisible.value = false
  previewTitle.value = ''
}
const handlePreview = async (file: any) => {
  if (!file.url && !file.preview) {
    file.preview = (await getBase64(file.originFileObj)) as string
  }
  previewImage.value = file.url || file.preview
  previewVisible.value = true
  previewTitle.value = file.name || file.url.substring(file.url.lastIndexOf('/') + 1)
}
const handleOk = async () => {
  try {
    await formRef.value?.validate()
    loading.value = true
    const params: BusinessCardEditParams = {
      follower: dataForm.follower,
      businessCardPath: dataForm.businessCardPath,
      id: rowsProps?.value?.id
    }
    const func = rowsProps?.value ? businessCardUpdate : businessCardCreate
    await func(params)
    DcMessage.success('操作成功！')
    emit('fetch')
    handleCancel()
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}
const handleCancel = () => {
  formRef.value?.resetFields()
  visible.value = false
}

defineExpose({ init })
</script>
<script lang="ts">
export default { name: 'ModalForm' }
</script>

<style scoped></style>
